import { Currency, CurrencyAmount, Price, Token } from '@kodiak-finance/sdk-core'
import {
  encodeSqrtRatioX96,
  FeeAmount,
  nearestUsableTick,
  Pool,
  Position,
  priceToClosestTick,
  TICK_SPACINGS,
  TickMath,
  tickToPrice,
} from '@kodiak-finance/v3-sdk'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import { usePool } from 'hooks/usePools'
import { PoolState } from 'hooks/usePools'
import JSBI from 'jsbi'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { ReactNode, useCallback, useEffect, useMemo } from 'react'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { getTickToPrice } from 'utils/getTickToPrice'

import { BIG_INT_ZERO } from '../../../constants/misc'
import { useCurrencyBalances } from '../../connection/hooks'
import { AppState } from '../../index'
import {
  Bound,
  Field,
  PresetRange,
  setFullRange,
  setPresetRange,
  setPresetTicks,
  typeInput,
  typeLeftRangeInput,
  typeLeftRangeTick,
  typeRightRangeInput,
  typeRightRangeTick,
  typeStartPriceInput,
} from './actions'
import { toSorted, tryParseTick } from './utils'

export function useV3MintState(): AppState['mintV3'] {
  return useAppSelector((state) => state.mintV3)
}

export function useV3MintActionHandlers(
  noLiquidity: boolean | undefined,
  token0?: Token,
  token1?: Token,
  feeAmount?: FeeAmount,
  invertPrice = false
): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
  onLeftRangeInput: (typedValue: string, opts?: { suppressUrl: boolean }) => void
  onRightRangeInput: (typedValue: string, opts?: { suppressUrl: boolean }) => void
  onStartPriceInput: (typedValue: string) => void
  onLeftRangeTickInput: (tick: number | undefined) => void
  onRightRangeTickInput: (tick: number | undefined) => void
} {
  const dispatch = useAppDispatch()
  const navigate = useChainAwareNavigate()
  const { search } = useLocation()

  const updateURL = useCallback(
    (key: string, value: string | null) => {
      const s = new URLSearchParams(search)
      if (value == null || value.trim() === '') {
        s.delete(key)
      } else {
        s.set(key, value)
      }
      navigate({ search: `?${s.toString()}` }, { replace: true })
    },
    [navigate, search]
  )

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onLeftRangeTickInput = useCallback(
    (tick: number | undefined, opts?: { suppressUrl?: boolean }) => {
      const setTick = invertPrice ? typeRightRangeTick : typeLeftRangeTick

      dispatch(setTick({ tick }))

      if (tick !== undefined && token0 && token1) {
        const pCanon = tickToPrice(token0, token1, tick)
        const pDisplay = invertPrice ? pCanon.invert() : pCanon
        const s = pDisplay.toSignificant(8)

        dispatch(typeLeftRangeInput({ typedValue: s }))
        if (!opts?.suppressUrl) updateURL('minPrice', s)
      } else {
        dispatch(typeLeftRangeInput({ typedValue: '' }))
        if (!opts?.suppressUrl) updateURL('minPrice', null)
      }
    },
    [invertPrice, dispatch, token0, token1, updateURL]
  )

  const onRightRangeTickInput = useCallback(
    (tick: number | undefined, opts?: { suppressUrl?: boolean }) => {
      const setTick = invertPrice ? typeLeftRangeTick : typeRightRangeTick

      dispatch(setTick({ tick }))

      if (tick !== undefined && token0 && token1) {
        const pCanon = tickToPrice(token0, token1, tick)
        const pDisplay = invertPrice ? pCanon.invert() : pCanon
        const s = pDisplay.toSignificant(8)

        dispatch(typeRightRangeInput({ typedValue: s }))
        if (!opts?.suppressUrl) updateURL('maxPrice', s)
      } else {
        dispatch(typeRightRangeInput({ typedValue: '' }))
        if (!opts?.suppressUrl) updateURL('maxPrice', null)
      }
    },
    [invertPrice, dispatch, token0, token1, updateURL]
  )

  const onLeftRangeInput = useCallback(
    (typedValue: string, opts?: { suppressUrl?: boolean }) => {
      dispatch(typeLeftRangeInput({ typedValue }))

      if (typedValue === '') {
        const setTick = invertPrice ? typeRightRangeTick : typeLeftRangeTick
        dispatch(setTick({ tick: undefined }))
      } else if (token0 && token1 && feeAmount) {
        const baseForParse = invertPrice ? token1 : token0
        const quoteForParse = invertPrice ? token0 : token1
        const tick = tryParseTick(baseForParse, quoteForParse, feeAmount, typedValue)
        const setTick = invertPrice ? typeRightRangeTick : typeLeftRangeTick
        dispatch(setTick({ tick }))
      }

      if (!opts?.suppressUrl) {
        updateURL('minPrice', typedValue)
      }
    },
    [dispatch, token0, token1, feeAmount, invertPrice, updateURL]
  )

  const onRightRangeInput = useCallback(
    (typedValue: string, opts?: { suppressUrl?: boolean }) => {
      dispatch(typeRightRangeInput({ typedValue }))

      if (typedValue === '') {
        const setTick = invertPrice ? typeLeftRangeTick : typeRightRangeTick
        dispatch(setTick({ tick: undefined }))
      } else if (token0 && token1 && feeAmount) {
        const baseForParse = invertPrice ? token1 : token0
        const quoteForParse = invertPrice ? token0 : token1
        const tick = tryParseTick(baseForParse, quoteForParse, feeAmount, typedValue)
        const setTick = invertPrice ? typeLeftRangeTick : typeRightRangeTick
        dispatch(setTick({ tick }))
      }

      if (!opts?.suppressUrl) {
        updateURL('maxPrice', typedValue)
      }
    },
    [dispatch, token0, token1, feeAmount, invertPrice, updateURL]
  )

  const onStartPriceInput = useCallback(
    (typedValue: string) => {
      dispatch(typeStartPriceInput({ typedValue }))
    },
    [dispatch]
  )

  return {
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onStartPriceInput,
    onLeftRangeTickInput,
    onRightRangeTickInput,
  }
}

export function useV3DerivedMintInfo(
  currencyA?: Currency,
  currencyB?: Currency,
  feeAmount?: FeeAmount,
  baseCurrency?: Currency,
  // override for existing position
  existingPosition?: Position
): {
  pool?: Pool | null
  poolState: PoolState
  ticks: { [bound in Bound]?: number | undefined }
  price?: Price<Token, Token>
  pricesAtTicks: {
    [bound in Bound]?: Price<Token, Token> | undefined
  }
  currencies: { [field in Field]?: Currency }
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  dependentField: Field
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  position: Position | undefined
  noLiquidity?: boolean
  errorMessage?: ReactNode
  invalidPool: boolean
  outOfRange: boolean
  invalidRange: boolean
  depositADisabled: boolean
  depositBDisabled: boolean
  invertPrice: boolean
  ticksAtLimit: { [bound in Bound]?: boolean | undefined }
  token0?: Token
  token1?: Token
} {
  const { account } = useConnection()

  const { independentField, typedValue, startPriceTypedValue, leftRangeTick, rightRangeTick, isFullRange } =
    useV3MintState()

  const dependentField = independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A

  const [tokenA, tokenB, baseToken] = useMemo(
    () => [currencyA?.wrapped, currencyB?.wrapped, baseCurrency?.wrapped],
    [currencyA, currencyB, baseCurrency]
  )

  const [token0, token1] = useMemo(
    () =>
      tokenA && tokenB ? (tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]) : [undefined, undefined],
    [tokenA, tokenB]
  )

  // currencies
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB,
    }),
    [currencyA, currencyB]
  )

  // balances
  const balances = useCurrencyBalances(
    account ?? undefined,
    useMemo(() => [currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B]], [currencies]),
    REFETCH_POLICY_FAST
  )
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1],
  }

  // pool
  const [poolState, pool] = usePool(currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B], feeAmount)
  const noLiquidity = poolState === PoolState.NOT_EXISTS

  // note to parse inputs in reverse
  const invertPrice = Boolean(baseToken && token0 && !baseToken.equals(token0))

  // always returns the price with 0 as base token
  const price: Price<Token, Token> | undefined = useMemo(() => {
    // if no liquidity use typed value
    if (noLiquidity) {
      const parsedQuoteAmount = tryParseCurrencyAmount(startPriceTypedValue, invertPrice ? token0 : token1)
      if (parsedQuoteAmount && token0 && token1) {
        const baseAmount = tryParseCurrencyAmount('1', invertPrice ? token1 : token0)
        const price =
          baseAmount && parsedQuoteAmount
            ? new Price(
                baseAmount.currency,
                parsedQuoteAmount.currency,
                baseAmount.quotient,
                parsedQuoteAmount.quotient
              )
            : undefined
        return (invertPrice ? price?.invert() : price) ?? undefined
      }
      return undefined
    } else {
      return pool && token0 ? pool.priceOf(token0) : undefined
    }
  }, [noLiquidity, startPriceTypedValue, invertPrice, token1, token0, pool])

  const invalidPrice = useMemo(() => {
    const sqrtRatioX96 = price ? encodeSqrtRatioX96(price.numerator, price.denominator) : undefined
    if (sqrtRatioX96) {
      console.log('debug: init sqrtRatioX96', sqrtRatioX96.toString())
    }
    return (
      price &&
      sqrtRatioX96 &&
      !(
        JSBI.greaterThanOrEqual(sqrtRatioX96, TickMath.MIN_SQRT_RATIO) &&
        JSBI.lessThan(sqrtRatioX96, TickMath.MAX_SQRT_RATIO)
      )
    )
  }, [price])

  const mockPool = useMemo(() => {
    if (tokenA && tokenB && feeAmount && price && !invalidPrice) {
      const currentTick = priceToClosestTick(price)
      const currentSqrt = TickMath.getSqrtRatioAtTick(currentTick)
      return new Pool(tokenA, tokenB, feeAmount, currentSqrt, JSBI.BigInt(0), currentTick, [])
    } else {
      return undefined
    }
  }, [feeAmount, invalidPrice, price, tokenA, tokenB])

  // if pool exists use it, if not use the mock pool
  const poolForPosition: Pool | undefined = pool ?? mockPool

  // lower and upper limits in the tick space for `feeAmoun<>
  const tickSpaceLimits: { [bound in Bound]: number | undefined } = useMemo(
    () => ({
      [Bound.LOWER]: feeAmount ? nearestUsableTick(TickMath.MIN_TICK, TICK_SPACINGS[feeAmount]) : undefined,
      [Bound.UPPER]: feeAmount ? nearestUsableTick(TickMath.MAX_TICK, TICK_SPACINGS[feeAmount]) : undefined,
    }),
    [feeAmount]
  )

  const ticks: { [bound in Bound]?: number | undefined } = useMemo(() => {
    if (isFullRange) {
      return {
        [Bound.LOWER]: tickSpaceLimits[Bound.LOWER],
        [Bound.UPPER]: tickSpaceLimits[Bound.UPPER],
      }
    }

    if (existingPosition) {
      return {
        [Bound.LOWER]: existingPosition.tickLower,
        [Bound.UPPER]: existingPosition.tickUpper,
      }
    }

    const lowerTick = leftRangeTick !== undefined ? leftRangeTick : tickSpaceLimits[Bound.LOWER]

    const upperTick = rightRangeTick !== undefined ? rightRangeTick : tickSpaceLimits[Bound.UPPER]

    return {
      [Bound.LOWER]: lowerTick,
      [Bound.UPPER]: upperTick,
    }
  }, [isFullRange, tickSpaceLimits, existingPosition, leftRangeTick, rightRangeTick])

  useEffect(() => {
    if (typeof ticks.LOWER === 'number' && typeof ticks.UPPER === 'number') {
      const sqrtLower = TickMath.getSqrtRatioAtTick(ticks.LOWER)
      const sqrtUpper = TickMath.getSqrtRatioAtTick(ticks.UPPER)

      console.log('debug: Ticks Data:', {
        lowerTick: ticks.LOWER,
        upperTick: ticks.UPPER,
        sqrtPriceX96Lower: sqrtLower.toString(),
        sqrtPriceX96Upper: sqrtUpper.toString(),
      })
    }
  }, [ticks.LOWER, ticks.UPPER])

  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks || {}

  // specifies whether the lower and upper ticks is at the exteme bounds
  const ticksAtLimit = useMemo(
    () => ({
      [Bound.LOWER]: feeAmount && tickLower === tickSpaceLimits.LOWER,
      [Bound.UPPER]: feeAmount && tickUpper === tickSpaceLimits.UPPER,
    }),
    [tickSpaceLimits, tickLower, tickUpper, feeAmount]
  )

  // mark invalid range
  const invalidRange = Boolean(typeof tickLower === 'number' && typeof tickUpper === 'number' && tickLower >= tickUpper)

  // always returns the price with 0 as base token
  const pricesAtTicks = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(token0, token1, ticks[Bound.LOWER]),
      [Bound.UPPER]: getTickToPrice(token0, token1, ticks[Bound.UPPER]),
    }
  }, [token0, token1, ticks])
  const { [Bound.LOWER]: lowerPrice, [Bound.UPPER]: upperPrice } = pricesAtTicks

  // liquidity range warning
  const outOfRange = Boolean(
    !invalidRange && price && lowerPrice && upperPrice && (price.lessThan(lowerPrice) || price.greaterThan(upperPrice))
  )

  // amounts
  const independentAmount: CurrencyAmount<Currency> | undefined = tryParseCurrencyAmount(
    typedValue,
    currencies[independentField]
  )

  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    // we wrap the currencies just to get the price in terms of the other token
    const wrappedIndependentAmount = independentAmount?.wrapped
    const dependentCurrency = dependentField === Field.CURRENCY_B ? currencyB : currencyA
    if (
      independentAmount &&
      wrappedIndependentAmount &&
      typeof tickLower === 'number' &&
      typeof tickUpper === 'number' &&
      poolForPosition
    ) {
      // if price is out of range or invalid range - return 0 (single deposit will be independent)
      if (outOfRange || invalidRange) {
        return undefined
      }

      if (feeAmount) {
        const spacing = TICK_SPACINGS[feeAmount]
        if (tickLower % spacing !== 0 || tickUpper % spacing !== 0) {
          console.log('Dependent amount calculation skipped: misaligned ticks')
          return undefined
        }
      }

      try {
        const position: Position | undefined = wrappedIndependentAmount.currency.equals(poolForPosition.token0)
          ? Position.fromAmount0({
              pool: poolForPosition,
              tickLower,
              tickUpper,
              amount0: independentAmount.quotient,
              useFullPrecision: true,
            })
          : Position.fromAmount1({
              pool: poolForPosition,
              tickLower,
              tickUpper,
              amount1: independentAmount.quotient,
            })

        const dependentTokenAmount = wrappedIndependentAmount.currency.equals(poolForPosition.token0)
          ? position.amount1
          : position.amount0
        return dependentCurrency && CurrencyAmount.fromRawAmount(dependentCurrency, dependentTokenAmount.quotient)
      } catch (error) {
        console.error('Dependent amount calculation failed:', error)
        return undefined
      }
    }

    return undefined
  }, [
    independentAmount,
    outOfRange,
    dependentField,
    currencyB,
    currencyA,
    tickLower,
    tickUpper,
    poolForPosition,
    invalidRange,
    feeAmount,
  ])

  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(() => {
    return {
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }
  }, [dependentAmount, independentAmount, independentField])

  // single deposit only if price is out of range
  const deposit0Disabled = Boolean(
    typeof tickUpper === 'number' && poolForPosition && poolForPosition.tickCurrent >= tickUpper
  )
  const deposit1Disabled = Boolean(
    typeof tickLower === 'number' && poolForPosition && poolForPosition.tickCurrent <= tickLower
  )

  // sorted for token order
  const depositADisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled && poolForPosition && tokenA && poolForPosition.token0.equals(tokenA)) ||
        (deposit1Disabled && poolForPosition && tokenA && poolForPosition.token1.equals(tokenA))
    )
  const depositBDisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled && poolForPosition && tokenB && poolForPosition.token0.equals(tokenB)) ||
        (deposit1Disabled && poolForPosition && tokenB && poolForPosition.token1.equals(tokenB))
    )

  // create position entity based on users selection
  const position: Position | undefined = useMemo(() => {
    if (
      !poolForPosition ||
      !tokenA ||
      !tokenB ||
      typeof tickLower !== 'number' ||
      typeof tickUpper !== 'number' ||
      invalidRange
    ) {
      return undefined
    }

    if (feeAmount) {
      const spacing = TICK_SPACINGS[feeAmount]

      // Check if ticks are properly aligned with current fee tier
      if (tickLower % spacing !== 0 || tickUpper % spacing !== 0) {
        console.log('Position creation skipped: ticks not aligned with fee tier spacing', {
          tickLower,
          tickUpper,
          spacing,
          feeAmount,
        })
        return undefined
      }
    }

    // mark as 0 if disabled because out of range
    const amount0 = !deposit0Disabled
      ? parsedAmounts?.[tokenA.equals(poolForPosition.token0) ? Field.CURRENCY_A : Field.CURRENCY_B]?.quotient
      : BIG_INT_ZERO
    const amount1 = !deposit1Disabled
      ? parsedAmounts?.[tokenA.equals(poolForPosition.token0) ? Field.CURRENCY_B : Field.CURRENCY_A]?.quotient
      : BIG_INT_ZERO

    if (amount0 !== undefined && amount1 !== undefined) {
      try {
        return Position.fromAmounts({
          pool: poolForPosition,
          tickLower,
          tickUpper,
          amount0,
          amount1,
          useFullPrecision: true,
        })
      } catch (error) {
        console.error('Position creation failed:', error)
        return undefined
      }
    } else {
      return undefined
    }
  }, [
    parsedAmounts,
    poolForPosition,
    tokenA,
    tokenB,
    deposit0Disabled,
    deposit1Disabled,
    invalidRange,
    tickLower,
    tickUpper,
    feeAmount,
  ])

  let errorMessage: ReactNode | undefined
  if (!account) {
    errorMessage = <>Connect Wallet</>
  }

  if (poolState === PoolState.INVALID) {
    errorMessage = errorMessage ?? <>Invalid pair</>
  }

  if (invalidPrice) {
    errorMessage = errorMessage ?? <>Invalid price input</>
  }

  if (
    (!parsedAmounts[Field.CURRENCY_A] && !depositADisabled) ||
    (!parsedAmounts[Field.CURRENCY_B] && !depositBDisabled)
  ) {
    errorMessage = errorMessage ?? <>Enter an amount</>
  }

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts

  if (currencyAAmount && currencyBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount)) {
    errorMessage = <>Insufficient {currencies[Field.CURRENCY_A]?.symbol} balance</>
  }

  if (currencyBAmount && currencyBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount)) {
    errorMessage = <>Insufficient {currencies[Field.CURRENCY_B]?.symbol} balance</>
  }

  const invalidPool = poolState === PoolState.INVALID

  return {
    dependentField,
    currencies,
    pool,
    poolState,
    currencyBalances,
    parsedAmounts,
    ticks,
    price,
    pricesAtTicks,
    position,
    noLiquidity,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
    token0,
    token1,
  }
}

export function useRangeHopCallbacks(
  baseCurrency: Currency | undefined,
  quoteCurrency: Currency | undefined,
  feeAmount: FeeAmount | undefined,
  tickLower: number | undefined,
  tickUpper: number | undefined,
  pool?: Pool | undefined | null,
  invertPrice?: boolean
) {
  const dispatch = useAppDispatch()

  const baseToken = useMemo(() => baseCurrency?.wrapped, [baseCurrency])
  const quoteToken = useMemo(() => quoteCurrency?.wrapped, [quoteCurrency])

  // enforce canonical token order for handlers
  const [sorted0, sorted1] = useMemo(() => toSorted(baseToken, quoteToken), [baseToken, quoteToken])

  // Handlers only need canonical tokens; UI min/max inversion is handled in RangeSelector
  const { onLeftRangeTickInput, onRightRangeTickInput } = useV3MintActionHandlers(
    false, // noLiquidity
    sorted0, // token0 (sorted)
    sorted1, // token1 (sorted)
    feeAmount,
    invertPrice
  )

  const spacing = feeAmount ? TICK_SPACINGS[feeAmount] : undefined

  // grid limits
  const lowerLimit = useMemo(() => (spacing ? nearestUsableTick(TickMath.MIN_TICK, spacing) : undefined), [spacing])
  const upperLimit = useMemo(() => (spacing ? nearestUsableTick(TickMath.MAX_TICK, spacing) : undefined), [spacing])

  // helpers
  const snap = useCallback((t: number) => (spacing ? nearestUsableTick(t, spacing) : t), [spacing])

  const clamp = useCallback(
    (t: number) => {
      if (lowerLimit === undefined || upperLimit === undefined) return t
      return Math.min(upperLimit, Math.max(lowerLimit, t))
    },
    [lowerLimit, upperLimit]
  )

  const getCurrentOrPool = useCallback(
    (t?: number) => (typeof t === 'number' ? t : typeof pool?.tickCurrent === 'number' ? pool!.tickCurrent : undefined),
    [pool?.tickCurrent]
  )

  // --- LOWER bound hops (must keep lower < upper) ---
  const getDecrementLower = useCallback(() => {
    if (!spacing) return
    const current = getCurrentOrPool(tickLower)
    if (typeof current !== 'number') return

    let next = clamp(snap(current - spacing))

    // prevent crossing
    if (typeof tickUpper === 'number' && next >= tickUpper) {
      next = clamp(tickUpper - spacing)
      if (next >= tickUpper) return // can't move
    }

    const handlerToCall = invertPrice ? onRightRangeTickInput : onLeftRangeTickInput
    handlerToCall(next)
  }, [
    spacing,
    getCurrentOrPool,
    tickLower,
    clamp,
    snap,
    tickUpper,
    invertPrice,
    onRightRangeTickInput,
    onLeftRangeTickInput,
  ])

  const getIncrementLower = useCallback(() => {
    if (!spacing) return
    const current = getCurrentOrPool(tickLower)
    if (typeof current !== 'number') return

    let next = clamp(snap(current + spacing))

    if (typeof tickUpper === 'number' && next >= tickUpper) {
      next = clamp(tickUpper - spacing)
      if (next >= tickUpper) return
    }

    const handlerToCall = invertPrice ? onRightRangeTickInput : onLeftRangeTickInput
    handlerToCall(next)
  }, [
    spacing,
    getCurrentOrPool,
    tickLower,
    clamp,
    snap,
    tickUpper,
    invertPrice,
    onRightRangeTickInput,
    onLeftRangeTickInput,
  ])

  // --- UPPER bound hops (must keep upper > lower) ---
  const getDecrementUpper = useCallback(() => {
    if (!spacing) return
    const current = getCurrentOrPool(tickUpper)
    if (typeof current !== 'number') return

    let next = clamp(snap(current - spacing))

    if (typeof tickLower === 'number' && next <= tickLower) {
      next = clamp(tickLower + spacing)
      if (next <= tickLower) return
    }

    const handlerToCall = invertPrice ? onLeftRangeTickInput : onRightRangeTickInput
    handlerToCall(next)
  }, [
    spacing,
    getCurrentOrPool,
    tickUpper,
    clamp,
    snap,
    tickLower,
    invertPrice,
    onLeftRangeTickInput,
    onRightRangeTickInput,
  ])

  const getIncrementUpper = useCallback(() => {
    if (!spacing) return
    const current = getCurrentOrPool(tickUpper)
    if (typeof current !== 'number') return

    let next = clamp(snap(current + spacing))

    if (typeof tickLower === 'number' && next <= tickLower) {
      next = clamp(tickLower + spacing)
      if (next <= tickLower) return
    }

    const handlerToCall = invertPrice ? onLeftRangeTickInput : onRightRangeTickInput
    handlerToCall(next)
  }, [
    spacing,
    getCurrentOrPool,
    tickUpper,
    clamp,
    snap,
    tickLower,
    invertPrice,
    onLeftRangeTickInput,
    onRightRangeTickInput,
  ])

  // SNAP WHEN FEE CHANGES (spacing grid changes)
  useEffect(() => {
    if (!spacing) return
    const lower = typeof tickLower === 'number' ? clamp(snap(tickLower)) : undefined
    const upper = typeof tickUpper === 'number' ? clamp(snap(tickUpper)) : undefined

    // no-op if unchanged
    if (lower === tickLower && upper === tickUpper) return

    // ensure non-crossing after snap; nudge outward if collapsed
    if (typeof lower === 'number' && typeof upper === 'number' && lower >= upper) {
      const nudgedLower = clamp(snap(lower - spacing))
      const nudgedUpper = clamp(snap(upper + spacing))
      onLeftRangeTickInput(nudgedLower)
      onRightRangeTickInput(nudgedUpper)
      return
    }

    onLeftRangeTickInput(lower)
    onRightRangeTickInput(upper)
  }, [spacing, tickLower, tickUpper, onLeftRangeTickInput, onRightRangeTickInput, snap, clamp, pool?.tickCurrent])

  const getSetFullRange = useCallback(() => {
    dispatch(setFullRange())
  }, [dispatch])

  return { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper, getSetFullRange }
}

interface UsePresetRangesParams {
  baseCurrency?: Currency | null
  quoteCurrency?: Currency | null
  token0?: Token
  token1?: Token
  feeAmount?: FeeAmount
  currentPrice?: Price<Token, Token>
  noLiquidity?: boolean
  invertPrice?: boolean
}

interface PresetRangeCalculation {
  leftTick?: number
  rightTick?: number
  leftPrice?: Price<Token, Token>
  rightPrice?: Price<Token, Token>
}

export function usePresetRanges({
  baseCurrency,
  quoteCurrency,
  feeAmount,
  currentPrice,
  noLiquidity,
  invertPrice,
}: UsePresetRangesParams) {
  const dispatch = useAppDispatch()

  const tokenA = baseCurrency?.wrapped
  const tokenB = quoteCurrency?.wrapped

  const PRESET_DELTA_TICKS: Record<PresetRange, number> = {
    [PresetRange.NARROW]: 100, // ±100 ticks ~1%
    [PresetRange.COMMON]: 2000, // ±2000 ticks ~20%
    [PresetRange.WIDE]: 5000, // ±5000 ticks ~50%
    [PresetRange.FULL]: -1,
    [PresetRange.CUSTOM]: -1,
  }

  const roundToSpacing = (t: number, s: number) => Math.max(s, Math.round(t / s) * s)

  const clampBand = (
    center: number,
    delta: number,
    minTick: number,
    maxTick: number,
    spacing: number
  ): [number, number] => {
    const width = delta * 2
    let lower = center - delta
    let upper = center + delta

    if (lower < minTick) {
      lower = minTick
      upper = minTick + width
    }
    if (upper > maxTick) {
      upper = maxTick
      lower = maxTick - width
    }

    lower = Math.max(minTick, Math.min(maxTick, nearestUsableTick(lower, spacing)))
    upper = Math.max(minTick, Math.min(maxTick, nearestUsableTick(upper, spacing)))

    if (lower >= upper) return [minTick, maxTick]
    return [lower, upper]
  }

  const calculatePresetRange = useCallback(
    (preset: PresetRange): PresetRangeCalculation => {
      if (!tokenA || !tokenB || !feeAmount || !currentPrice || noLiquidity) return {}

      try {
        const [sorted0, sorted1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
        const spacing = TICK_SPACINGS[feeAmount]
        const minTick = nearestUsableTick(TickMath.MIN_TICK, spacing)
        const maxTick = nearestUsableTick(TickMath.MAX_TICK, spacing)

        if (preset === PresetRange.FULL) {
          const leftPrice = tickToPrice(sorted0, sorted1, minTick)
          const rightPrice = tickToPrice(sorted0, sorted1, maxTick)
          return { leftTick: minTick, rightTick: maxTick, leftPrice, rightPrice }
        }

        const canonical = invertPrice ? currentPrice.invert() : currentPrice
        const centerTick = nearestUsableTick(priceToClosestTick(canonical), spacing)

        const deltaTarget = PRESET_DELTA_TICKS[preset]
        const deltaSnapped = roundToSpacing(deltaTarget, spacing)

        const [lowerTick, upperTick] = clampBand(centerTick, deltaSnapped, minTick, maxTick, spacing)

        const leftPrice = tickToPrice(sorted0, sorted1, lowerTick)
        const rightPrice = tickToPrice(sorted0, sorted1, upperTick)

        return { leftTick: lowerTick, rightTick: upperTick, leftPrice, rightPrice }
      } catch (e) {
        console.error('preset calc error', e)
        return {}
      }
    },
    [tokenA, tokenB, feeAmount, currentPrice, noLiquidity, invertPrice]
  )

  const presetRanges = useMemo(
    () =>
      ({
        [PresetRange.NARROW]: calculatePresetRange(PresetRange.NARROW),
        [PresetRange.COMMON]: calculatePresetRange(PresetRange.COMMON),
        [PresetRange.WIDE]: calculatePresetRange(PresetRange.WIDE),
        [PresetRange.FULL]: calculatePresetRange(PresetRange.FULL),
        [PresetRange.CUSTOM]: {},
      } as Record<PresetRange, PresetRangeCalculation>),
    [calculatePresetRange]
  )

  const applyPreset = useCallback(
    (preset: PresetRange) => {
      if (noLiquidity) {
        dispatch(setPresetRange({ preset: PresetRange.FULL }))
        return
      }

      if (preset === PresetRange.CUSTOM) return

      if (preset === PresetRange.FULL) {
        dispatch(setPresetRange({ preset: PresetRange.FULL }))
        return
      }

      const range = presetRanges[preset]
      if (range.leftTick == null || range.rightTick == null) return

      dispatch(setPresetTicks({ preset, leftTick: range.leftTick, rightTick: range.rightTick }))
    },
    [noLiquidity, dispatch, presetRanges]
  )

  return { presetRanges, applyPreset, PRESET_DELTA_TICKS }
}
